import { FileSpreadsheet, FileText, Download } from "lucide-react";
import React, { useState } from "react";
import { toast } from "react-toastify";
import CustomModal from "../../../../components/CustomModal";
import api from "../../../../services/api";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentFilters: any;
}

const ExportModal: React.FC<Props> = ({ isOpen, onClose, currentFilters }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [formatType, setFormatType] = useState<"pdf" | "excel">("pdf");
  const [loading, setLoading] = useState(false);

  const dateTimeWithSeconds = (value: string) => (
    value.length === 16 ? `${value}:00` : value
  );

  const handleExport = async () => {
    if (!startDate || !endDate) {
      toast.warning("Selecione o período completo.");
      return;
    }

    try {
      setLoading(true);

      const filtersToSend: any = { ...currentFilters };
      
      const body = {
        ...filtersToSend,
        data_agendamento_terminal_1: dateTimeWithSeconds(startDate),
        data_agendamento_terminal_2: dateTimeWithSeconds(endDate),
        data_agendamento_terminal1: dateTimeWithSeconds(startDate),
        data_agendamento_terminal2: dateTimeWithSeconds(endDate),
        data_inicio: dateTimeWithSeconds(startDate),
        data_fim: dateTimeWithSeconds(endDate),
        tipo_arquivo: formatType === "excel" ? "xls" : "pdf",
        order_by: "data_agendamento_terminal",
        order_direction: "desc",
      };

      const response = await api.post(
        "/listar/operacaoPortoAgendada/exportar",
        body,
        {
          responseType: "blob",
        }
      );

      const mimeTypes: any = {
        pdf: "application/pdf",
        excel: "application/vnd.ms-excel"
      };

      const blob = new Blob([response.data], { type: mimeTypes[formatType] });
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      let extension = formatType === "excel" ? "xls" : "pdf";
      let nomeArquivo = `operacao_porto_agendada.${extension}`;

      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches?.[1]) {
          nomeArquivo = matches[1].replaceAll("'", "").replaceAll('"', '');
          if (formatType === "excel") {
            nomeArquivo = nomeArquivo.replace(/\.csv$/i, ".xls");
          }
        }
      }
      
      link.setAttribute("download", nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.remove(); // Remove do DOM
      globalThis.URL.revokeObjectURL(url); // Limpa a memória

      toast.success("Exportação realizada com sucesso!");
      onClose();
    } catch (error) {
      console.error("Erro na exportação:", error);
      toast.error("Erro ao exportar arquivo.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      title="Exportar Operações"
      onClose={onClose}
    >
      <div className="p-8 flex flex-col gap-6 w-full">
        <div className="-mt-6 mb-2">
            <p className="text-[#666] text-sm font-normal">Selecione o período e o formato do arquivo.</p>
        </div>

        <div className="flex flex-col gap-3">
          <label className="text-xs font-bold text-[#666] uppercase tracking-wider">Período</label>
          <div className="flex gap-4">
            <div className="flex flex-col w-full">
              <span className="text-xs text-[#666] font-medium mb-1.5 ml-1">Início</span>
              <div className="bg-[#F5F6FA] rounded-xl px-4 py-3 border border-transparent focus-within:border-[#062D4E] transition-colors">
                <input
                    type="datetime-local"
                    step={1}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full bg-transparent text-sm text-[#1E2121] focus:outline-none font-medium"
                />
              </div>
            </div>
            <div className="flex flex-col w-full">
              <span className="text-xs text-[#666] font-medium mb-1.5 ml-1">Fim</span>
              <div className="bg-[#F5F6FA] rounded-xl px-4 py-3 border border-transparent focus-within:border-[#062D4E] transition-colors">
                <input
                    type="datetime-local"
                    step={1}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full bg-transparent text-sm text-[#1E2121] focus:outline-none font-medium"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-2">
          <label className="text-xs font-bold text-[#666] uppercase tracking-wider">Formato do Arquivo</label>
          <div className="grid grid-cols-2 gap-4">
            <div 
                onClick={() => setFormatType("pdf")}
                className={`relative flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 group ${
                    formatType === "pdf" 
                    ? "border-[#E5E7EB] bg-white ring-1 ring-[#062D4E] shadow-sm" 
                    : "border-[#F5F6FA] bg-[#F5F6FA] hover:border-[#E5E7EB]"
                }`}
            >
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#FFF1F2] text-[#E11D48] mb-2">
                    <FileText size={20} className="stroke-[2.5]" />
                </div>
                <span className="font-semibold text-[#1E2121] text-center">PDF</span>
                <div className={`absolute top-2 right-2 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                     formatType === "pdf" ? "border-[#062D4E]" : "border-gray-300"
                }`}>
                    {formatType === "pdf" && <div className="w-2 h-2 rounded-full bg-[#062D4E]" />}
                </div>
            </div>

            <div 
                onClick={() => setFormatType("excel")}
                className={`relative flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 group ${
                    formatType === "excel" 
                    ? "border-[#E5E7EB] bg-white ring-1 ring-[#062D4E] shadow-sm" 
                    : "border-[#F5F6FA] bg-[#F5F6FA] hover:border-[#E5E7EB]"
                }`}
            >
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#ECFDF5] text-[#10B981] mb-2">
                    <FileSpreadsheet size={20} className="stroke-[2.5]" />
                </div>
                <span className="font-semibold text-[#1E2121] text-center">Excel</span>
                 <div className={`absolute top-2 right-2 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                     formatType === "excel" ? "border-[#062D4E]" : "border-gray-300"
                }`}>
                    {formatType === "excel" && <div className="w-2 h-2 rounded-full bg-[#062D4E]" />}
                </div>
            </div>
            </div>
          </div>

        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-[#1E2121] border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={handleExport}
            className="px-8 py-2.5 bg-[#062D4E] text-white rounded-lg hover:bg-[#0a3d6a] transition-colors text-sm font-semibold flex items-center gap-2 shadow-lg shadow-[#062D4E]/20"
            disabled={loading}
          >
            <Download size={18} />
            {loading ? "Exportando..." : "Exportar"}
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

export default ExportModal;
